/**
 * The row actions on an appointment, each one a Queue 1 request: record the tenant's answer, hold /
 * extend / release, cancel, start service, no show, unable to complete, reopen (administrators, with a
 * reason). Reschedule opens the Book sheet. The menu offers only what the visit's status allows; the
 * processor still has the last word and its words are shown as it wrote them.
 *
 * 1.5.16: on Today this sheet is the office's "More ⋯" (never offered to the crew). "Record permission received by
 * phone" is here beside "Tenant won't be home": who gave it, kept with who recorded it and when.
 *
 * 1.5.17: on Today the sheet is "Office action" (`office`) — what a tenant asks the office for by phone, for an
 * administrator or a co-administrator only. Two items stand first: "Tenant cancelled today (by phone)" (one press;
 * no email unless "Send the tenant a confirmation" is pressed on the step that follows) and "Permission to enter
 * without the tenant home" (email the request, or record it by phone: the terms on screen to read aloud, the
 * tenant's name, the staff member's own, and the tick). The two permission items are no longer the scheduler's.
 */
import { useMemo, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { OFFICE_ROLES, type CancelConfirmationResponse, type PermissionPhoneRequest, type PermissionPhoneResponse, type PermissionTermsResponse } from '@shared/office-action';
import type { AppointmentRow, TxSubmitResponse } from '@shared/api-types';
import type { Role } from '@shared/roles';
import { vocab } from '@shared/vocab';
import { serviceDateKey } from '@shared/dates';
import { newFormSession, type CancelInitiator, type ReasonCategory } from '@shared/envelopes/queue1';
import { apiFetch, ApiRequestError } from '@/lib/api';
import { formatClockOf, formatDayShort } from '@/lib/dates';
import type { ArrivalMarker } from '@shared/arrival';
import { pressName, type UndoReason } from '@shared/press';
import { UndoFields, useUndoPress } from '@/features/press/Press';
import { useTx } from '@/lib/tx';
import { pressWords } from '@/lib/press-words';
import { useAnnounce } from '@/components/Announcer';
import { Icon } from '@/components/Icons';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { SheetContent } from '@/components/ui/sheet';

export type ActionKind = 'menu' | 'answer' | 'hold' | 'extend' | 'release' | 'cancel' | 'start' | 'noShow' | 'unable' | 'reopen' | 'reschedule' | 'entry' | 'entryPhone' | 'phoneCancel' | 'moveDay' | 'permission' | 'clearArrival';

const isOffice = (role: Role): boolean => (OFFICE_ROLES as readonly Role[]).includes(role);

const V = vocab.SM_AppointmentTransactions;
const INITIATORS = (V.CancellationInitiatedBy as readonly string[]).filter((x) => x !== 'Not Applicable') as CancelInitiator[];
const REASONS = (V.OutcomeReasonCategory as readonly string[]).filter((x) => x !== 'Not Applicable') as ReasonCategory[];
const METHODS = ['Phone Call', 'In Person', 'Email', 'Text Message', 'Other'] as const;
const ANSWERS = ['Confirmed', 'Declined', 'Unable To Reach'] as const;

/** What the status allows (REQUIREMENTS.md §3 lifecycle); the processor re-checks every one. */
export function actionsFor(a: Pick<AppointmentRow, 'status' | 'confirmation' | 'serviceDateKey' | 'kind'>, role: Role, today = serviceDateKey()): ActionKind[] {
  const out: ActionKind[] = [];
  const arrived = a.serviceDateKey <= today;
  // 1.6.4: a facility space (the office, a shared room) that is still to be cleaned can be moved to another day —
  // the one thing its menu holds; closed, an administrator can reopen it (1.6.3)
  if (a.kind === 'facility' && a.status === 'Scheduled') return ['reschedule'];
  switch (a.status) {
    case 'Proposed':
      out.push('answer', 'reschedule', 'hold', 'cancel');
      break;
    case 'Scheduled':
      if (a.confirmation !== 'Confirmed' && a.kind === 'tenant') out.push('answer');
      if (arrived) out.push('start', 'noShow', 'unable');
      out.push('reschedule', 'hold', 'cancel');
      // Tenant won't be home (1.4.0): ask the tenant, on their own link, for permission to enter.
      // 1.5.16: or write down that they gave it by phone. 1.5.17: administrators and co-administrators only.
      if (a.kind === 'tenant' && isOffice(role)) out.push('entry', 'entryPhone');
      break;
    case 'On Hold':
      out.push('extend', 'release', 'cancel');
      break;
    case 'In Progress':
      out.push('noShow', 'unable');
      break;
    case 'Completed':
    case 'No Show':
    case 'Unable to Complete':
      if (role === 'admin') out.push('reopen');
      break;
  }
  return out;
}

/**
 * The Office action sheet's menu (1.5.17): the two things a tenant phones about first — cancelling today's visit,
 * and permission to enter (its two ways are one step further) — then what the visit's status allows, as ever.
 */
export function officeActionsFor(a: Pick<AppointmentRow, 'status' | 'confirmation' | 'serviceDateKey' | 'kind'> & { arrival?: Pick<ArrivalMarker, 'answer'> | null; closeout?: unknown }, role: Role, today = serviceDateKey()): ActionKind[] {
  const rest = actionsFor(a, role, today);
  // 1.6.5: "Clear the arrival stamp" — an Arrived pressed by mistake, after the minutes for Undo (never a visit
  // cancelled at the door, never one that has been closed out)
  if (isOffice(role) && a.arrival && a.arrival.answer !== 'doorCancel' && !a.closeout && (a.status === 'Scheduled' || a.status === 'In Progress')) rest.push('clearArrival');
  const first: ActionKind[] = [];
  if (a.status === 'Scheduled' && a.kind === 'tenant' && a.serviceDateKey === today) {
    first.push('phoneCancel');
    // 1.5.19: the tenant who phones to cancel can be moved instead, to any day or another time today: the Book sheet
    // as a reschedule (nothing is cancelled). The visit's own Reschedule stays in the list below, as ever.
    if (isOffice(role)) first.push('moveDay');
  }
  if (rest.includes('entry')) first.push('permission');
  return [...first, ...rest.filter((k) => k !== 'entry' && k !== 'entryPhone')];
}

/** Where a kind's words live: the two 1.5.17 items (and the by-phone form's Save) under `office17`. */
const wordsKey = (k: ActionKind, part: 'title' | 'help' | 'submit'): string =>
  k === 'clearArrival' ? `press165.clearArrival.${part}` : k === 'phoneCancel' || k === 'permission' || k === 'moveDay' ? `office17.${k}.${part}` : k === 'entryPhone' && part === 'submit' ? 'office17.phone.submit' : `actions.${k}.${part}`;

function localInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function ActionSheet({
  appt,
  role,
  initial,
  office,
  initialNote,
  onDone,
  onReschedule,
  returnFocusTo,
}: {
  appt: AppointmentRow;
  role: Role;
  initial: ActionKind;
  /** 1.5.17: opened as "Office action" (Today) — honoured for an administrator or a co-administrator only. */
  office?: boolean;
  /** 24 Oct 2026: the Inbox's "Cancel their visit" opens the cancel sheet with the reply's context in the note. */
  initialNote?: string;
  onDone: () => void;
  onReschedule: () => void;
  returnFocusTo?: RefObject<HTMLElement | null>;
}) {
  const { t, i18n } = useTranslation();
  const announce = useAnnounce();
  const toast = useToast();
  const tx = useTx();
  const qc = useQueryClient();
  const [kind, setKind] = useState<ActionKind>(initial);
  // A form session per form opened (a new one when the person comes back to it).
  const session = useMemo(() => newFormSession(), [kind]); // eslint-disable-line react-hooks/exhaustive-deps
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [initiatedBy, setInitiatedBy] = useState<CancelInitiator>('Tenant');
  const [reason, setReason] = useState<ReasonCategory>('Tenant Unavailable');
  const [note, setNote] = useState(initialNote ?? '');
  const [answer, setAnswer] = useState<(typeof ANSWERS)[number]>('Confirmed');
  const [method, setMethod] = useState<(typeof METHODS)[number]>('Phone Call');
  const [disposition, setDisposition] = useState<'Retain Until Hold Until' | 'Release Immediately'>('Retain Until Hold Until');
  const maxHold = useMemo(() => new Date(Date.now() + 48 * 3600_000), []);
  const [holdUntil, setHoldUntil] = useState(localInput(maxHold));
  const [unableReason, setUnableReason] = useState<ReasonCategory | ''>('');
  const [reopenReason, setReopenReason] = useState('');
  const [reopenCategory, setReopenCategory] = useState<ReasonCategory>('Scheduling Error');
  // 1.5.17 — Office action
  const asOffice = !!office && isOffice(role);
  /** After "Mark cancelled": the outcome in words; the sheet stays for "Send the tenant a confirmation". */
  const [cancelled, setCancelled] = useState<string | null>(null);
  const [confirmQueued, setConfirmQueued] = useState<string | null>(null);
  const [termsLang, setTermsLang] = useState<'en' | 'es' | null>(null);
  const [tenantName, setTenantName] = useState('');
  const [staffName, setStaffName] = useState('');
  const [read, setRead] = useState(false);
  const terms = useQuery({
    queryKey: ['permission-terms', appt.appointmentId],
    queryFn: () => apiFetch<PermissionTermsResponse>(`/api/housekeeping/office/permission-terms?appointmentId=${encodeURIComponent(appt.appointmentId)}`),
    enabled: kind === 'permission' || kind === 'entryPhone',
    staleTime: 60_000,
    retry: false,
  });
  const lang = termsLang ?? terms.data?.tenantLang ?? 'en';
  const noEmail = !!terms.data && !terms.data.tenantHasEmail;

  const available = asOffice ? officeActionsFor(appt, role) : actionsFor(appt, role);
  const day = formatDayShort(appt.serviceDateKey, i18n.language);
  const who = `${appt.unit} · ${day}`;

  /** "Tenant won't be home": queues the tenant's email with their own link (the cancel window applies). */
  const askEntry = async () => {
    setBusy(true);
    setError(null);
    try {
      await apiFetch<{ status: string; scheduledAt: string }>('/api/housekeeping/entry-permission', { method: 'POST', body: JSON.stringify({ appointmentId: appt.appointmentId }) });
      const msg = t('actions.entry.queued', { unit: appt.unit });
      toast(msg);
      announce(msg);
      onDone();
    } catch (e) {
      const m = e instanceof ApiRequestError ? (e.body?.message ?? e.message) : (e as Error).message;
      setError(m);
      announce(m, 'assertive');
    } finally {
      setBusy(false);
    }
  };

  const failed = (e: unknown) => {
    const m = e instanceof ApiRequestError ? (e.body?.message ?? e.message) : (e as Error).message;
    setError(m);
    announce(m, 'assertive');
  };

  /**
   * "Record it by phone" (1.5.17): staff read the terms aloud and the tenant agreed — kept on the visit at once with
   * both names, as a one-page document; the copy for the record is queued by this press (the Outbox, the cancel window).
   */
  const recordPhone = async () => {
    setBusy(true);
    setError(null);
    try {
      const body: PermissionPhoneRequest = { appointmentId: appt.appointmentId, tenantName: tenantName.trim(), staffName: staffName.trim(), lang, readAndAgreed: true };
      const res = await apiFetch<PermissionPhoneResponse>('/api/housekeeping/office/permission-phone', { method: 'POST', body: JSON.stringify(body) });
      // the server's note stands beside "done" too: a copy that went to the Director and the office only (no Cynthia set)
      const msg = res.copyCommunicationId ? [t('office17.phone.done', { unit: appt.unit }), res.copyNote].filter(Boolean).join(' ') : t('office17.phone.doneNoCopy', { unit: appt.unit, note: res.copyNote ?? '' }).trim();
      toast(msg);
      announce(msg);
      for (const k of ['today', 'attention', 'messages']) void qc.invalidateQueries({ queryKey: [k] });
      onDone();
    } catch (e) {
      failed(e);
    } finally {
      setBusy(false);
    }
  };

  /** "Tenant cancelled today (by phone)" (1.5.17): one press; the sheet stays for the confirmation — no email by this press. */
  const phoneCancel = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch<TxSubmitResponse>('/api/housekeeping/office/tenant-cancelled', { method: 'POST', body: JSON.stringify({ session, appointmentId: appt.appointmentId }) });
      tx.track(res, { label: t('office17.phoneCancel.label', { who }), unit: appt.unit, appointmentId: appt.appointmentId, dayKey: appt.serviceDateKey });
      const msg = res.status === 'duplicate' ? t('tx.duplicate') : t('office17.phoneCancel.done', { unit: appt.unit });
      setCancelled(msg);
      announce(msg);
      void qc.invalidateQueries({ queryKey: ['today'] });
    } catch (e) {
      failed(e);
    } finally {
      setBusy(false);
    }
  };

  /** "Send the tenant a confirmation" — the second press: queued in the Outbox with the cancel window. */
  const sendConfirmation = async () => {
    setBusy(true);
    setError(null);
    try {
      await apiFetch<CancelConfirmationResponse>('/api/housekeeping/office/cancel-confirmation', { method: 'POST', body: JSON.stringify({ appointmentId: appt.appointmentId }) });
      const msg = t('office17.phoneCancel.confirmQueued', { unit: appt.unit });
      setConfirmQueued(msg);
      toast(msg);
      announce(msg);
      void qc.invalidateQueries({ queryKey: ['messages'] });
    } catch (e) {
      failed(e);
    } finally {
      setBusy(false);
    }
  };

  const send = async (body: Record<string, unknown>, label: string) => {
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch<TxSubmitResponse>('/api/transactions/appointment', {
        method: 'POST',
        body: JSON.stringify({ session, appointmentId: appt.appointmentId, ...body }),
      });
      tx.track(res, { label, unit: appt.unit, appointmentId: appt.appointmentId, dayKey: appt.serviceDateKey });
      const msg = pressWords(t, res, t('tx.sent', { label }));
      toast(msg);
      announce(msg);
      onDone();
    } catch (e) {
      const m = e instanceof ApiRequestError ? (e.body?.message ?? e.message) : (e as Error).message;
      setError(m);
      announce(m, 'assertive');
    } finally {
      setBusy(false);
    }
  };

  /** 1.6.5: "Clear the arrival stamp" — the same undo the crew has for its first minutes, with the office's name. */
  const stamp = (appt as AppointmentRow & { arrival?: ArrivalMarker | null }).arrival ?? null;
  const [undoReason, setUndoReason] = useState<UndoReason>('mistake');
  const [undoWords, setUndoWords] = useState('');
  const undoPress = useUndoPress();
  const clearArrival = async () => {
    setBusy(true);
    setError(null);
    const failed = await undoPress('arrived', appt, undoReason, undoWords);
    setBusy(false);
    if (failed) setError(failed);
    else onDone();
  };

  const labelFor = (k: ActionKind) => t(`actions.label.${k}`, { who });

  const submit = () => {
    switch (kind) {
      case 'cancel':
        return send({ op: 'cancel', initiatedBy, reason, note: note.trim() || undefined }, labelFor('cancel'));
      case 'answer':
        return send({ op: 'answer', outcome: answer, method, note: note.trim() || undefined }, t('actions.label.answerOf', { who, answer: t(`actions.answer.${answer}`) }));
      case 'hold':
        return send(
          {
            op: 'hold',
            disposition,
            holdUntil: disposition === 'Retain Until Hold Until' ? new Date(holdUntil).toISOString() : null,
            note: note.trim() || undefined,
          },
          labelFor('hold'),
        );
      case 'extend':
        return send({ op: 'extend' }, labelFor('extend'));
      case 'release':
      case 'start':
      case 'noShow':
        return send({ op: kind, note: note.trim() || undefined }, labelFor(kind));
      case 'unable':
        return send({ op: 'unable', reason: unableReason || undefined, note: note.trim() || undefined }, labelFor('unable'));
      case 'reopen':
        return send({ op: 'reopen', reason: reopenReason.trim(), category: reopenCategory }, labelFor('reopen'));
      case 'entry':
        return askEntry();
      case 'entryPhone':
        return recordPhone();
      case 'phoneCancel':
        return phoneCancel();
      case 'clearArrival':
        return clearArrival();
      default:
        return undefined;
    }
  };

  const canSubmit = !busy && (kind !== 'reopen' || reopenReason.trim().length >= 3) && (kind !== 'entryPhone' || (!!terms.data && tenantName.trim().length >= 2 && staffName.trim().length >= 2 && read));

  const select = (id: string, label: string, value: string, options: readonly string[], onChange: (v: string) => void, prefix: string, blank?: string) => (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <select id={id} className="input" value={value} onChange={(e) => onChange(e.target.value)}>
        {blank !== undefined ? <option value="">{blank}</option> : null}
        {options.map((o) => (
          <option key={o} value={o}>
            {t(`${prefix}.${o}`, { defaultValue: o })}
          </option>
        ))}
      </select>
    </div>
  );

  const noteField = (label = t('actions.noteOpt')) => (
    <div className="field">
      <label htmlFor="action-note">{label}</label>
      <textarea id="action-note" className="input" value={note} onChange={(e) => setNote(e.target.value)} />
    </div>
  );

  const body = () => {
    switch (kind) {
      case 'menu':
        return available.length ? (
          <ul className="action-menu" data-testid="action-menu">
            {available.map((k) => (
              <li key={k}>
                <button
                  type="button"
                  className="action-item"
                  onClick={() => (k === 'reschedule' || k === 'moveDay' ? onReschedule() : setKind(k))}
                  data-testid={`action-${k}`}
                >
                  <b>{t(wordsKey(k, 'title'))}</b>
                  <span>{t(wordsKey(k, 'help'))}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="help">{t('actions.none')}</p>
        );
      case 'cancel':
        return (
          <>
            <p className="help">{t('actions.cancel.explain')}</p>
            {select('cancel-by', t('actions.cancel.by'), initiatedBy, INITIATORS, (v) => setInitiatedBy(v as CancelInitiator), 'initiatedByLabel')}
            {select('cancel-reason', t('actions.cancel.reason'), reason, REASONS, (v) => setReason(v as ReasonCategory), 'reasonLabel')}
            {noteField()}
          </>
        );
      case 'answer':
        return (
          <>
            <fieldset className="field">
              <legend>{t('actions.answer.question')}</legend>
              <div className="radio-cards">
                {ANSWERS.map((a) => (
                  <label key={a}>
                    <input type="radio" name="answer" checked={answer === a} onChange={() => setAnswer(a)} />
                    <span>
                      {t(`actions.answer.${a}`)}
                      <small>{t(`actions.answer.helpOf.${a}`, { status: appt.status })}</small>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
            {select('answer-method', t('actions.answer.method'), method, METHODS, (v) => setMethod(v as (typeof METHODS)[number]), 'method')}
            {noteField()}
          </>
        );
      case 'hold':
        return (
          <>
            <fieldset className="field">
              <legend>{t('actions.hold.question')}</legend>
              <div className="radio-cards">
                <label>
                  <input type="radio" name="disp" checked={disposition === 'Retain Until Hold Until'} onChange={() => setDisposition('Retain Until Hold Until')} />
                  <span>
                    {t('actions.hold.retain')}
                    <small>{t('actions.hold.retainHelp')}</small>
                  </span>
                </label>
                <label>
                  <input type="radio" name="disp" checked={disposition === 'Release Immediately'} onChange={() => setDisposition('Release Immediately')} />
                  <span>
                    {t('actions.hold.release')}
                    <small>{t('actions.hold.releaseHelp')}</small>
                  </span>
                </label>
              </div>
            </fieldset>
            {disposition === 'Retain Until Hold Until' ? (
              <div className="field">
                <label htmlFor="hold-until">{t('actions.hold.until')}</label>
                <input
                  id="hold-until"
                  className="input"
                  type="datetime-local"
                  value={holdUntil}
                  max={localInput(maxHold)}
                  onChange={(e) => setHoldUntil(e.target.value)}
                />
                <p className="help">{t('actions.hold.untilHelp')}</p>
              </div>
            ) : null}
            {noteField()}
          </>
        );
      case 'entry':
        return (
          <>
            <p className="help">{t('actions.entry.explain')}</p>
            <p className="help">{t('actions.entry.perVisit')}</p>
          </>
        );
      case 'entryPhone':
        return terms.data ? (
          <>
            <div className="permission-read">
              <h3 className="eyebrow" id="permission-read-h">
                {t('office17.phone.read')}
              </h3>
              <div className="seg" role="group" aria-label={t('office17.phone.lang')}>
                {(['en', 'es'] as const).map((l) => (
                  <button key={l} type="button" lang={l} aria-pressed={lang === l} onClick={() => setTermsLang(l)} data-testid={`permission-lang-${l}`}>
                    {t(`office17.phone.${l}`)}
                  </button>
                ))}
              </div>
            </div>
            <section className="permission-terms" lang={lang} aria-labelledby="permission-terms-h" data-testid="permission-terms">
              <h3 id="permission-terms-h">{terms.data[lang].title}</h3>
              {terms.data[lang].paragraphs.map((p, n) => (
                <p key={n}>{p}</p>
              ))}
            </section>
            <div className="field">
              <label htmlFor="permission-tenant-name">{t('office17.phone.tenantName')}</label>
              <input id="permission-tenant-name" className="input" required aria-required="true" maxLength={120} autoComplete="off" value={tenantName} onChange={(e) => setTenantName(e.target.value)} data-testid="permission-tenant-name" />
            </div>
            <div className="field">
              <label htmlFor="permission-staff-name">{t('office17.phone.staffName')}</label>
              <input id="permission-staff-name" className="input" required aria-required="true" maxLength={120} autoComplete="name" value={staffName} onChange={(e) => setStaffName(e.target.value)} data-testid="permission-staff-name" />
            </div>
            <label className="office-tick">
              <input type="checkbox" required aria-required="true" checked={read} onChange={(e) => setRead(e.target.checked)} data-testid="permission-read" />
              <span>{t('office17.phone.agreed')}</span>
            </label>
            <p className="help">{t('office17.phone.kept')}</p>
          </>
        ) : terms.isError ? (
          <div className="warn take" role="alert" data-testid="permission-terms-error">
            <Icon name="alert" />
            <div>{terms.error instanceof ApiRequestError ? (terms.error.body?.message ?? t('office17.phone.failed')) : t('office17.phone.failed')}</div>
          </div>
        ) : (
          <p className="help" role="status">
            {t('office17.phone.loading')}
          </p>
        );
      // 1.5.17: the two ways — "some people don't have emails"
      case 'permission':
        return (
          <>
            <p className="help">{t('office17.permission.lead')}</p>
            <ul className="action-menu" data-testid="permission-ways">
              <li>
                <button type="button" className="action-item" disabled={noEmail} aria-describedby="permission-email-help" onClick={() => setKind('entry')} data-testid="permission-email">
                  <b>{t('office17.permission.email')}</b>
                  <span id="permission-email-help">
                    {noEmail ? (
                      <>
                        <Icon name="lock" /> {t('office17.permission.noEmail')}
                      </>
                    ) : (
                      t('office17.permission.emailHelp')
                    )}
                  </span>
                </button>
              </li>
              <li>
                <button type="button" className="action-item" onClick={() => setKind('entryPhone')} data-testid="permission-phone">
                  <b>{t('office17.permission.phone')}</b>
                  <span>{t('office17.permission.phoneHelp')}</span>
                </button>
              </li>
            </ul>
          </>
        );
      case 'phoneCancel':
        return cancelled ? (
          <>
            <div className="warn ok" data-testid="phoneCancel-done">
              <Icon name="check" />
              <div>{cancelled}</div>
            </div>
            <div className="chips">
              <Button type="button" variant="secondary" disabled={busy || !!confirmQueued} onClick={() => sendConfirmation()} data-testid="phoneCancel-confirm-email">
                <Icon name="mail" />
                {t('office17.phoneCancel.sendConfirm')}
              </Button>
            </div>
            {confirmQueued ? (
              <p className="help" data-testid="phoneCancel-confirm-queued">
                {confirmQueued}
              </p>
            ) : null}
          </>
        ) : (
          <p className="help">{t('office17.phoneCancel.explain')}</p>
        );
      case 'clearArrival':
        return (
          <>
            {stamp ? <p className="help" data-testid="clear-arrival-explain">{t('press165.clearArrival.explain', { name: pressName(stamp.by), time: formatClockOf(stamp.at, i18n.language) })}</p> : null}
            <UndoFields reason={undoReason} words={undoWords} onReason={setUndoReason} onWords={setUndoWords} />
          </>
        );
      case 'extend':
        return <p className="help">{t('actions.extend.explain')}</p>;
      case 'release':
        return <p className="help">{t('actions.release.explain')}</p>;
      case 'start':
        return <p className="help">{t('actions.start.explain')}</p>;
      case 'noShow':
        return (
          <>
            <p className="help">{t('actions.noShow.explain')}</p>
            {noteField()}
          </>
        );
      case 'unable':
        return (
          <>
            <p className="help">{t('actions.unable.explain')}</p>
            {select('unable-reason', t('actions.unable.reason'), unableReason, REASONS, (v) => setUnableReason(v as ReasonCategory | ''), 'reasonLabel', t('actions.unable.noReason'))}
            {noteField()}
          </>
        );
      case 'reopen':
        return (
          <>
            <p className="help">{t('actions.reopen.explain')}</p>
            <div className="field">
              <label htmlFor="reopen-reason">{t('actions.reopen.reason')}</label>
              <textarea
                id="reopen-reason"
                className="input"
                required
                aria-required="true"
                aria-describedby={appt.kind === 'tenant' ? undefined : 'reopen-no-checks'}
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
              />
              {/* 1.6.3: a staff unit or an office is reopened without a tenant's checks (Queue 1 v8n) */}
              {appt.kind === 'tenant' ? null : (
                <p className="help" id="reopen-no-checks" data-testid="reopen-no-checks">
                  {t(appt.kind === 'staff' ? 'actions.reopen.staffUnit' : 'actions.reopen.office')}
                </p>
              )}
            </div>
            {select('reopen-cat', t('actions.reopen.category'), reopenCategory, REASONS, (v) => setReopenCategory(v as ReasonCategory), 'reasonLabel')}
          </>
        );
      default:
        return null;
    }
  };

  // 1.5.17: in Office action the two ways lead back to the step that offered them
  const back: ActionKind = asOffice && (kind === 'entry' || kind === 'entryPhone') ? 'permission' : 'menu';
  const backButton = (
    <Button variant="secondary" type="button" onClick={() => (initial === 'menu' ? setKind(back) : onDone())}>
      {initial === 'menu' ? t('actions.back') : t('book.cancel')}
    </Button>
  );

  return (
    <SheetContent
      title={kind === 'menu' ? (asOffice ? t('office17.title', { who }) : t('actions.menuTitle', { who })) : `${t(wordsKey(kind, 'title'))} · ${who}`}
      returnFocusTo={returnFocusTo}
      data-testid="action-sheet"
      footer={
        kind === 'menu' || (kind === 'phoneCancel' && cancelled) ? (
          <Button variant={kind === 'menu' ? 'secondary' : undefined} type="button" onClick={onDone} data-testid="action-close">
            {t('app.close')}
          </Button>
        ) : kind === 'permission' ? (
          backButton
        ) : (
          <>
            {backButton}
            <Button type="button" onClick={() => submit()} disabled={!canSubmit} data-testid="action-submit">
              {busy ? t('book.sending') : t(wordsKey(kind, 'submit'))}
            </Button>
          </>
        )
      }
    >
      {asOffice && kind === 'menu' ? (
        <p className="help" data-testid="office-lead">
          {t('office17.lead')}
        </p>
      ) : null}
      <p className="help" data-testid="action-who">
        <b>{appt.unit}</b> · {appt.tenantName} · {day}
      </p>
      {body()}
      {error ? (
        <div className="warn take" role="alert" data-testid="action-error">
          <Icon name="alert" />
          <div>{error}</div>
        </div>
      ) : null}
    </SheetContent>
  );
}
